/**
 * @GestionalePD Admin Surveys page UI
 */
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  IconButton,
  TableCell,
  TableFooter,
  TableBody,
  Card,
  Typography,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputAdornment,
  Tooltip,
  TablePagination,
} from '@mui/material';
import { styled } from '@mui/system';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PollRoundedIcon from '@mui/icons-material/PollRounded';
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded';
import BeenhereRoundedIcon from '@mui/icons-material/BeenhereRounded';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Navigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
//-----------------------------------------------------------------------------------------------
import {
  PopAnimation,
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_DELAY,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from '../../utils/popUpStyles';
import { PaginationAPI, theme } from '../../utils/TableStyles';
import { DefaultContainedButton, DefaultOutlinedButton, CustomIconButton } from '../../utils/Buttons';
import { CustomSnackbar, CreateSnackbarFunctions } from '../../utils/Snackbar';
import { CustomDatePicker, CustomTextField } from '../../utils/StyledInputs';
import { HttpErrorHandler } from '../../utils/ErrorHandler';
import { LoadingUI } from '../../utils/LayoutStyles';
import SurveyDiagram from '../../sections/app/SurveyDiagram';
import { EntitiesDialog } from '../../utils/EntitiesDialog';
import { CommonLabel } from '../../utils/Common';
import API from '../../api';

/**
 * @returns Admin Q&A page UI
 */

// Custom popup style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

dayjs.locale('it');

export default function Surveys() {
  const [currentProfile, setCurrentProfile] = useState('');

  // General
  const navigate = useNavigate();
  const is600Screen = useMediaQuery('(min-width: 600px)');

  // Surveys Info
  const [surveyList, setSurveyList] = useState([]);
  const [surveyStats, setSurveyStats] = useState([]);
  const [apiResponseData, setApiResponseData] = useState({});
  const [surveyApiCalled, setSurveyApiCalled] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [openNewSurveyDialog, setOpenNewSurveyDialog] = useState(false);

  // Open New Survey Dialog
  const handleOpenNewSurveyDialog = () => {
    setOpenNewSurveyDialog(true);
  };

  // Close New Survey Dialog
  const handleCloseNewSurveyDialog = () => {
    setOpenNewSurveyDialog(false);
    setSelectedEntity(null);
    setOpen(false);
  };

  // Get Initial APIS
  useEffect(() => {
    getActiveSurveyCount();
    getInactiveSurveyCount();
    getSurveys(true);
    getSurveyStats();
  }, []);

  // Get Survey Stats API
  const getSurveyStats = () => {
    API.get(`/surveys_stats`)
      .then((response) => {
        setSurveyStats(response.data);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Create Survey Data For Cake Diagram
  const createSurveyData = (survey) => {
    const newArray = survey.answers.map((answer) => ({
      label: answer.answer_name,
      value: answer.answer_votes,
    }));

    return {
      series: newArray,
    };
  };

  // Survey Counts
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Set Survey Total Number
  useEffect(() => {
    const total = inactiveCount + activeCount;
    setTotalCount(total);
  }, [activeCount, inactiveCount]);

  // Get Active Survey Count
  const getActiveSurveyCount = () => {
    API.get('/surveys/countActiveSurvey')
      .then((response) => {
        setActiveCount(response.data.active_surveys);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Get Inactive Survey Count
  const getInactiveSurveyCount = () => {
    API.get('/surveys/countInactiveSurvery')
      .then((response) => {
        setInactiveCount(response.data.inactive_surveys);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Get Surveys List
  const getSurveys = (initial) => {
    API.get('/surveys')
      .then((response) => {
        setApiResponseData(response);

        const sortedData = response.data.sort((a, b) => {
          if (a.survey_status === 1 && b.survey_status !== 1) {
            return -1;
          }
          if (a.survey_status !== 1 && b.survey_status === 1) {
            return 1;
          }
          return 0;
        });
        setSurveyList(sortedData);

        if (initial) {
          setSurveyApiCalled(true);
        }
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
        setSurveyApiCalled(true);
      })
      .finally(() => {
        setInitialLoading(false);
      });
  };

  /* ------------------------------- New Survey ------------------------------- */
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [onlyCardHolder, setOnlyCardHolder] = useState(0);
  const [surveyStatus, setSurveyStatus] = useState(1);
  const [question, setQuestion] = useState('');
  const [surveyName, setSurveyName] = useState('');

  const [selectedEntity, setSelectedEntity] = useState(null);
  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
    setOpen(false);
  };

  const getSelectedEntity = (selectedEntity) => {
    setSelectedEntity(selectedEntity);
  };

  // Create New Survey
  const createNewSurvey = (e) => {
    e.preventDefault();

    const newSurveyData = {
      survey_name: surveyName,
      survey_question: question,
      entity_id: selectedEntity.id,
      survey_status: surveyStatus,
      start_date: dayjs(startDate).format('YYYY/MM/DD'),
      end_date: dayjs(endDate).format('YYYY/MM/DD'),
      only_card_holder: onlyCardHolder,
    };

    let itemsLeft = false;

    Object.keys(newSurveyData).forEach((key) => {
      const value = newSurveyData[key];

      if (Object.prototype.hasOwnProperty.call(newSurveyData, key)) {
        if (
          value === undefined ||
          value === null ||
          value === 'Invalid Date' ||
          (typeof value === 'string' && value.length === 0)
        ) {
          itemsLeft = true;
          handleOpenNewSurveySnack();
        }
      }
    });

    if (!itemsLeft) {
      if (dayjs(newSurveyData.end_date).isBefore(dayjs(newSurveyData.start_date))) {
        showPopup(false, 'La data di fine non può essere antecedente a quella di inizio');
      } else {
        API.post('/surveys', newSurveyData)
          .then((response) => {
            getSurveys(false);
            showPopup(true, 'Informazioni salvate');

            setTimeout(() => {
              handleCloseNewSurveyDialog();
            }, POPUP_DURATION_DELAY);
          })
          .catch((error) => {
            HttpErrorHandler(error, setPopUpText);
            showPopup(false);
          });
      }
    }
  };

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);

  // Table Empty Rows
  const emptyRows =
    page > 0
      ? rowsPerPage === 5 && page % 2 === 0
        ? Math.max(0, 1 * rowsPerPage - surveyList.length)
        : rowsPerPage === 5 && page % 2 !== 0
        ? Math.max(0, 1 * rowsPerPage + 5 - surveyList.length)
        : rowsPerPage === 10
        ? Math.max(0, 1 * rowsPerPage - surveyList.length)
        : 0
      : 0;

  // change page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Snackbar
  const {
    open: openNewSurveySnack,
    handleOpen: handleOpenNewSurveySnack,
    handleClose: handleCloseNewSurveySnack,
  } = CreateSnackbarFunctions(false);

  // popup handler
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popupSuccess, setPopupSuccess] = useState('');

  // Show Popup
  const showPopup = (success, text) => {
    setPopupSuccess(success);
    setOpen(true);

    if (text) {
      setPopUpText(text);
    }

    setTimeout(
      () => {
        setOpen(false);
      },
      success ? POPUP_DURATION_SUCCESS : POPUP_DURATION_ERROR
    );
  };

  // popup component
  const displayPopup = () => {
    return (
      <Container ref={setAnchor} sx={{ position: 'absolute', bottom: '-20px' }}>
        <Popup anchor={anchor} open={open}  >
          {popupSuccess ? (
            <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
          ) : (
            <PopupErrorBody>{popUpText}</PopupErrorBody>
          )}
        </Popup>
      </Container>
    );
  };

  useEffect(() => {
    setSurveyList(apiResponseData.data);
  }, [apiResponseData]);

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  return (
    <div>
      <Helmet>
        <title>Questionari</title>
      </Helmet>
      {initialLoading ? (
        LoadingUI()
      ) : (
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ padding: '24px', position: 'relative', border: '2px solid rgb(44, 128, 255, .3)' }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '.8em' }}>Questionari totali:</Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '2em' }}>{totalCount}</Typography>
                <IconButton
                  disableRipple
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: '24px',
                    transform: 'translateY(-50%)',
                    cursor: 'default',
                    backgroundColor: '#f6f6f6',
                    borderRadius: '10px',
                  }}
                >
                  <PollRoundedIcon sx={{ color: '#2c80ff', fontSize: '2em' }} />
                </IconButton>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ padding: '24px', position: 'relative', border: '2px solid rgb(0, 128, 0, .3)' }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '.8em' }}>Questionari aperti:</Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '2em' }}>{activeCount}</Typography>
                <IconButton
                  disableRipple
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: '24px',
                    transform: 'translateY(-50%)',
                    cursor: 'default',
                    backgroundColor: '#f6f6f6',
                    borderRadius: '10px',
                  }}
                >
                  <HowToVoteRoundedIcon sx={{ color: 'green', fontSize: '2em' }} />
                </IconButton>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ padding: '24px', position: 'relative', border: '2px solid rgb(255, 0, 0, .3)' }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '.8em' }}>Questionari chiusi:</Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '2em' }}>{inactiveCount}</Typography>
                <IconButton
                  disableRipple
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: '24px',
                    transform: 'translateY(-50%)',
                    cursor: 'default',
                    backgroundColor: '#f6f6f6',
                    borderRadius: '10px',
                  }}
                >
                  <BeenhereRoundedIcon sx={{ color: 'red', fontSize: '2em' }} />
                </IconButton>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ paddingTop: '24px', paddingBottom: '24px' }}>
            {surveyStats.map((survey, index) => (
              <Grid key={index} item xs={12} md={6} lg={4}>
                <SurveyDiagram title={survey.survey_name} chart={createSurveyData(survey)} />
              </Grid>
            ))}
          </Grid>

          <Stack direction="row" justifyContent="end">
            <EntitiesDialog
              openDialog={openEntitiesFilter}
              closeDialog={handleCloseEntitiesFilterDialog}
              outputEntity={getSelectedEntity}
              context={'survey'}
            />

            <DefaultContainedButton onClick={handleOpenNewSurveyDialog}>Nuovo Questionario</DefaultContainedButton>

            <Dialog fullWidth onClose={() => {}} open={openNewSurveyDialog}>
              <DialogTitle>Nuovo Questionatio</DialogTitle>
              <DialogContent sx={{ paddingTop: '10px !important' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <CustomTextField
                      required
                      onChange={(e) => setSurveyName(e.currentTarget.value)}
                      label="Nome Questionario"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <CustomTextField
                      required
                      onChange={(e) => setQuestion(e.currentTarget.value)}
                      rows="3"
                      fullWidth
                      multiline
                      label="Domanda questionario"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {!selectedEntity ? (
                      <DefaultOutlinedButton
                        customStyles={{ width: '100%' }}
                        onClick={() => setOpenEntitiesFilter(!openEntitiesFilter)}
                      >
                        Filtra Entità
                      </DefaultOutlinedButton>
                    ) : (
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <CustomTextField
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                                  <Tooltip title="Modifica" placement="left" arrow>
                                    <EditIcon onClick={() => setOpenEntitiesFilter(!openEntitiesFilter)} />
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                            value={selectedEntity.name}
                            label="Entità"
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <CustomDatePicker
                          required
                          onChange={(date) => setStartDate(date)}
                          label="Data Inizio"
                          format="DD/MM/YYYY"
                        />
                      </LocalizationProvider>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <CustomDatePicker
                          required
                          onChange={(date) => setEndDate(date)}
                          label="Data Fine"
                          format="DD/MM/YYYY"
                        />
                      </LocalizationProvider>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      required
                      select
                      label="Autorizzazione Voto"
                      defaultValue={0}
                      onChange={(e) => setOnlyCardHolder(e.target.value)}
                    >
                      <MenuItem value={0}>Chiunque</MenuItem>
                      <MenuItem value={1}>Solo Tesserati</MenuItem>
                    </CustomTextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      required
                      select
                      label="Stato Questionario"
                      defaultValue={1}
                      onChange={(e) => setSurveyStatus(e.target.value)}
                    >
                      <MenuItem value={1}>Aperto</MenuItem>
                      <MenuItem value={0}>Chiuso</MenuItem>
                    </CustomTextField>
                  </Grid>
                </Grid>

                {displayPopup()}
              </DialogContent>
              <DialogActions>
                <DefaultContainedButton onClick={createNewSurvey}>Crea</DefaultContainedButton>
                <DefaultOutlinedButton onClick={handleCloseNewSurveyDialog}>Chiudi</DefaultOutlinedButton>

                <CustomSnackbar open={openNewSurveySnack} handleClose={handleCloseNewSurveySnack} />
              </DialogActions>
            </Dialog>
          </Stack>

          <Card sx={{ marginTop: '24px', padding: '24px 24px 0px' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Stato</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Votanti</TableCell>
                    <TableCell>Voti</TableCell>
                    <TableCell>Esito</TableCell>
                    <TableCell>Data Apertura</TableCell>
                    <TableCell>Data Chiusura</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>

                {surveyApiCalled ? (
                  surveyList.length > 0 ? (
                    <TableBody>
                      {surveyList.map((survey) => (
                        <TableRow key={survey.id}>
                          <TableCell>
                            <CommonLabel
                              textColor={survey.survey_status ? '#118d57' : '#b71d18'}
                              bgColor={survey.survey_status ? '#d3efdf' : '#f7ddd8'}
                              text={survey.survey_status ? 'Aperto' : 'Chiuso'}
                            />
                          </TableCell>
                          <TableCell>{survey.survey_name}</TableCell>
                          <TableCell>{survey.n_users}</TableCell>
                          <TableCell>{survey.n_answers}</TableCell>
                          <TableCell>{survey.win_answer}</TableCell>
                          <TableCell>{survey.start_date}</TableCell>
                          <TableCell>{survey.end_date}</TableCell>
                          <TableCell>
                            <CustomIconButton
                              size="small"
                              onClick={() =>
                                navigate('/admin/surveys/survey-details', { state: { surveyID: survey.id } })
                              }
                            >
                              <KeyboardArrowRightIcon />
                            </CustomIconButton>
                          </TableCell>
                        </TableRow>
                      ))}

                      {emptyRows > 0 && (
                        <TableRow style={{ height: 47 * emptyRows }}>
                          <TableCell colSpan={7} />
                        </TableRow>
                      )}
                    </TableBody>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: 'center' }}>
                          Nessun questionario trovato
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={7}>{LoadingUI()}</TableCell>
                    </TableRow>
                  </TableBody>
                )}

                <TableFooter>
                  <TableRow>
                    <ThemeProvider theme={theme}>
                      <TablePagination
                        sx={{ border: 0 }}
                        rowsPerPageOptions={[]}
                        count={
                          apiResponseData && Object.keys(apiResponseData).length > 0 ? apiResponseData.data.length : 0
                        }
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={(props) => (
                          <PaginationAPI
                            {...props}
                            elements={10}
                            apiResponseData={apiResponseData}
                            setApiResponseData={setApiResponseData}
                            lastPage={apiResponseData.last_page}
                          />
                        )}
                      />
                    </ThemeProvider>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Card>
        </Container>
      )}
    </div>
  );
}
